import { AbstractChart } from '@tw/types';
import * as _ from 'lodash';
import moment from '@tw/moment-cached/module/timezone';

export const emptyArray = Object.freeze([]);
export const emptyObject = Object.freeze({});
const shipping = { costType: 'country' };

export const getStats = (state) => state.stats || emptyArray;

export const getNewStats = (state) => {
  return state.newStats || emptyObject;
};

export const getActiveShippingProvider = (state) => state?.activeShippingProvider || 'shopify';

export const getNewStatsService =
  (service: string, isMsp = false) =>
  (state) => {
  if(isMsp) {
    service = state.msp ?? 'shopify'
  }
    return state.newStats?.[service] || emptyArray;
  };

export const isOldStats = (stats) => Array.isArray(stats) || !stats;
export const getPaymentGatewayCosts = (state) => state.paymentGatewayCosts || emptyArray;
export const getViewThrough = (state) => state.viewThrough || emptyObject;
export const getProducts = (state) => state.products || emptyArray;
export const getShippingCosts = (state) => state.shippingCosts || emptyArray;
export const getShippingCostsType = (state) => state.shipping || shipping;
export const getCustomSpends = (state) => state.customSpends || emptyArray;
export const getMainDatePickerSelectionRange = (state) => state.mainDatePickerSelectionRange;
export const totalInventoryItems = (state) => state.totalInventoryItems;
export const msp = (state) => state.msp;
export const totalInventoryItemsMissing = (state) => state.totalInventoryItemsMissing;
export const totalInventoryCost = (state) => state.totalInventoryCost;
export const totalInventoryValue = (state) => state.totalInventoryValue;
export const willySummaryBox = (state) => state.willySummaryBox || emptyArray;
export const timezone = (state) => state.shopTimezone;
export const isServer = (state) => state.isServer ?? false;
export const getCustomMetrics = (state) => state.customMetrics;
export const getCustomSpendCategories = (state) => state.customSpendCategories || emptyArray;
export const getCustomExpenseAttributionType = (state) =>
  state.customExpenseAttributionType || 'daily';
export const getCustomExpenseSmartAttribution = (state) =>
  state.customExpenseSmartAttribution || emptyObject;
export const getExternalApps = (state) => state.externalApps || emptyArray;
export const isOneDay = (state) => state.isOneDay;
export const selectedStatsServices = (state) => state.selectedStatsServcies || emptyArray;
export const getPreCalculatedUniqueCustomerCount = (state) =>
  state.ltv && state.ltv.uniqueCustomerCount;
export const useShippingChargesAsCost = (state) => state.useShippingChargesAsCost ?? false;

export function safeDivide(numerator, denominator) {
  if (!numerator || !denominator) {
    return 0;
  }
  if (!isFinite(numerator) || !isFinite(denominator)) {
    return 0;
  }
  return numerator / denominator || 0;
}

export function avg(stats, param): number {
  if (!stats) return 0;
  let count = 0;
  let sum = stats.reduce(
    (dayTotal, day) =>
      dayTotal +
      Object.keys(day.hours || {}).reduce((hourTotal, hour) => {
        count++;
        return hourTotal + (+day.hours[hour][param] || 0);
      }, 0),
    0
  );
  return sum / count;
}

export function total(stats, param): number {
  if (!stats) return 0;
  return stats.reduce(
    (dayTotal, day) =>
      dayTotal +
      Object.keys(day.hours || {}).reduce(
        (hourTotal, hour) => hourTotal + (+day.hours[hour][param] || 0),
        0
      ),
    0
  );
}

export function totalWithAlternative(stats, param1, param2): number {
  if (!stats) return 0;
  return stats.reduce(
    (dayTotal, day) =>
      dayTotal +
      Object.keys(day.hours || {}).reduce(
        (hourTotal, hour) => hourTotal + (day.hours[hour][param1] || day.hours[hour][param2] || 0),
        0
      ),
    0
  );
}

export function sumArray(arrToSum) {
  return arrToSum.filter((a) => a).reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
}

export function last(stats, param) {
  if (!stats) return 0;
  for (let index = stats.length - 1; index >= 0; index--) {
    const { hours } = stats[index];
    for (let hIndex = Object.keys(hours).length - 1; hIndex >= 0; hIndex--) {
      const hour = hours[hIndex];
      if (hour && hour[param]) {
        return hour[param];
      }
    }
  }
  return 0;
}

export const _mergeAndSumObject = (agg, obj) => {
  Object.entries(obj).forEach(([id, val]) => {
    if (val && typeof val == 'object') {
      if (!agg) agg = {};
      if (!agg[id]) agg[id] = {};
      _mergeAndSumObject(agg[id], val);
    }
    if (typeof val == 'number') {
      if (!agg[id]) agg[id] = 0;
      agg[id] += val;
    }
    if (typeof val == 'string') {
      if (isNaN(+val)) {
        agg[id] = val;
      } else {
        if (!agg[id]) agg[id] = 0;
        agg[id] += Number(val);
      }
    }
  });
};

export const mergeAndSumObject = (stats, prop) => {
  var data = {};
  stats.forEach((day) =>
    Object.keys(day.hours).forEach((hour) => {
      if (day.hours[hour] && day.hours[hour][prop]) {
        _mergeAndSumObject(data, day.hours[hour][prop]);
        // Object.entries(day.hours[hour][prop]).forEach(([id, count]) => {
        //   if (!data[id]) data[id] = 0;
        //   data[id] += count;
        // })
      }
    })
  );
  return data;
};

export const sum = (obj: number[]): number => {
  return Object.values(obj).reduce((t, v) => t + v, 0);
};

/**
 * Take specific stat and create an { x, y } series for the chart
 * @returns { x: number, y: number }
 */
export const simpleChart = (stats, param, settings: { fill24?: boolean } = {}): AbstractChart[] => {
  var chart = [];
  if (!stats || !stats.length) return [];
  if (stats.length === 1) {
    // one day, chart based on hours
    var day = stats[0];
    var keys = Object.keys(day.hours);
    keys.forEach((hour) => {
      var x = +hour;
      var val = day.hours[hour][param];
      if (val && typeof val == 'object') val = sum(val);
      var y = val || 0;
      chart.push({ x, y });
    });
    if (settings.fill24) {
      for (var x = (keys.length && +keys[keys.length - 1]) || 23; x < 24 - 1; x++) {
        chart.push({ x, y: 0 });
      }
    }
  } else {
    var maxX = 0;
    stats.forEach((day) => {
      var x = moment(day.end).dayOfYear();
      if (x >= maxX) maxX = x;
      else x += maxX;
      var y = 0;
      Object.keys(day.hours).forEach((hour) => {
        var val = day.hours[hour][param];
        if (val && typeof val == 'object') val = sum(val);
        y += val || 0;
      });
      chart.push({ x, y });
    });
  }
  return chart;
};

//TODO remove this after we will fix the recalc mongo for all shops
export const simpleChartWithAlternative = (
  stats,
  param1,
  param2,
  settings: { fill24?: boolean } = {}
): AbstractChart[] => {
  var chart = [];
  if (!stats || !stats.length) return [];
  if (stats.length === 1) {
    // one day, chart based on hours
    var day = stats[0];
    var keys = Object.keys(day.hours);
    keys.forEach((hour) => {
      var x = +hour;
      var val = day.hours[hour][param1] || day.hours[hour][param2];
      if (val && typeof val == 'object') val = sum(val);
      var y = val || 0;
      chart.push({ x, y });
    });
    if (settings.fill24) {
      for (var x = (keys.length && +keys[keys.length - 1]) || 23; x < 24 - 1; x++) {
        chart.push({ x, y: 0 });
      }
    }
  } else {
    var maxX = 0;
    stats.forEach((day) => {
      var x = moment(day.end).dayOfYear();
      if (x >= maxX) maxX = x;
      else x += maxX;
      var y = 0;
      Object.keys(day.hours).forEach((hour) => {
        var val = day.hours[hour][param1] || day.hours[hour][param2];
        if (val && typeof val == 'object') val = sum(val);
        y += val || 0;
      });
      chart.push({ x, y });
    });
  }
  return chart;
};

export const simpleChartWithManipulateFunction = (
  stats,
  param,
  manipulateFunction,
  settings: { fill24?: boolean } = {}
) => {
  var chart = [];
  if (!stats || !stats.length) return [];
  if (stats.length === 1) {
    // one day, chart based on hours
    var day = stats[0];
    var keys = Object.keys(day.hours);
    keys.forEach((hour) => {
      var x = +hour;
      var val = day.hours[hour][param];
      if (val && typeof val == 'object') val = sum(val);
      var y = manipulateFunction(val || 0);
      chart.push({ x, y });
    });
    if (settings.fill24) {
      for (var x = (keys.length && +keys[keys.length - 1]) || 23; x < 24 - 1; x++) {
        chart.push({ x, y: 0 });
      }
    }
  } else {
    var maxX = 0;
    stats.forEach((day) => {
      var x = moment(day.end).dayOfYear();
      if (x >= maxX) maxX = x;
      else x += maxX;
      var y = 0;
      Object.keys(day.hours).forEach((hour) => {
        var val = day.hours[hour][param];
        if (val && typeof val == 'object') val = sum(val);
        y += manipulateFunction(val || 0);
      });
      chart.push({ x, y });
    });
  }
  return chart;
};

export const sortChart = (a, b) => (a.x > b.x ? 1 : b.x > a.x ? -1 : 0);
export const singleDayChart = (stats, param) => {
  const charts: Record<string, Record<'count' | 'revenue', AbstractChart[]>> = {};
  var day = stats[0];
  const hours = Object.entries(day.hours);
  hours.forEach(([hour, obj]) => {
    const segmentGroup = obj[param];
    const x = +hour;
    Object.entries<any>(segmentGroup || {}).forEach(([id, value]) => {
      if (!charts[id]) {
        charts[id] = {
          count: [],
          revenue: [],
        };
      }
      const y = value.revenue || 0;
      charts[id].revenue.push({ x, y });
    });
  });
  Object.values(charts).forEach((chart) => {
    for (let x = 0; x < 24; x++) {
      if (!chart.revenue.find((h) => h.x === x)) {
        chart.revenue.push({ x, y: 0 });
      }
    }
    chart.revenue.sort(sortChart);
  });
  return charts;
};

export const multipleDaysChart = (stats, param) => {
  const charts: Record<string, Record<'count' | 'revenue', AbstractChart[]>> = {};
  let maxX = 0;
  let minX = 366;
  stats.forEach((day) => {
    let x = moment(day.start).dayOfYear();
    if (x < minX) minX = x;

    if (x > maxX) maxX = x;
    else x += maxX;
    Object.values(day.hours).forEach((obj) => {
      const segmentGroup = obj[param];
      Object.entries<any>(segmentGroup || {}).forEach(([id, value]) => {
        if (!charts[id]) {
          charts[id] = {
            count: [],
            revenue: [],
          };
        }
        const y = value.revenue || 0;
        charts[id].revenue.push({ x, y });
      });
    });
  });
  Object.values(charts).forEach((chart) => {
    for (let x = minX; x <= maxX; x++) {
      if (!chart.revenue.find((h) => h.x === x)) {
        chart.revenue.push({ x, y: 0 });
      }
    }
    chart.revenue.sort(sortChart);
  });
  return charts;
};

export const salesBySegmentChart = (stats, param) => {
  if (stats?.[param]) {
    const charts: Record<string, Record<'count' | 'revenue', AbstractChart[]>> = {};
    Object.entries(stats[param]).forEach(([id, obj]) => {
      charts[id] = {
        revenue: simpleChart(obj, 'total_price'),
        count: simpleChart(obj, 'count'),
      };
    });
    return charts;
  } else return {};
};

export const convertWillyToMetricsBreakdown = (xs, ys) => {
  const combined = xs.map((date, index) => ({
    date: date,
    metrics: {
      metric: ys[index],
    },
  }));

  return {
    data: [
      {
        metricsBreakdown: combined,
      },
    ],
  };
};

/**
 * Takes 2 charts of another stats and create chart based on those 2 by dividing them in multiplier
 * @param chart1 first chart
 * @param chart2 second chart
 * @param {any} settings the multiplier factor in chart1yMultiplier property
 * @returns
 */
export const simpleChart2 = (
  chart1: AbstractChart[],
  chart2: AbstractChart[],
  settings: { chart1yMultiplier?: number } = {}
): AbstractChart[] => {
  try {
    var { chart1yMultiplier } = settings;
    var chart = [];
    chart1.forEach(({ x, y }) => {
      var _y = chart2.find((a) => a.x === x)?.y;
      if (_y) _y = (y * (chart1yMultiplier || 1)) / _y;
      else _y = 0;
      chart.push({
        x,
        y: _y,
      });
    });
    return chart;
  } catch (e) {
    console.warn('Chart crash', e);
    return [];
  }
};

/**
 * Takes 2 or more charts and summarize them in order to create an { x, y } series based on the summarization
 * @param chart1 first chart
 * @param chart2 second chart
 * @param moreCharts array of more charts to summarize
 * @param {any} settings
 * @returns
 */
export const simpleChart3 = (chart1, chart2, moreCharts = []): AbstractChart[] => {
  // merge and sum all charts
  const mergedCharts = [...chart1, ...chart2, ...moreCharts.flat()].reduce((acc, { x, y }) => {
    if (!acc[x]) acc[x] = 0;
    acc[x] += y || 0;
    return acc;
  }, {});
  return Object.entries(mergedCharts)
    .map(([x, y]) => ({ x: +x, y: +y }))
    .sort(sortChart);
};

// MINUS Y's
export const simpleChartMinus = (chart1, chart2, moreCharts = []) => {
  var chart = [];
  chart1.forEach(({ x, y }) => {
    var _y = chart2.find((a) => a.x === x)?.y ?? 0;
    _y = (y || 0) - (_y || 0);
    if (moreCharts.length) {
      moreCharts.forEach((ch) => {
        _y -= ch.find((a) => a.x === x)?.y ?? 0;
      });
    }

    chart.push({
      x,
      y: _y,
    });
  });

  return chart;
};

export const simpleChartPlus = (chart1, chart2, moreCharts = []) => {
  var chart = [];
  chart1.forEach(({ x, y }, index) => {
    var _y = chart2[index]?.y;
    _y = (y || 0) + (_y || 0);
    if (moreCharts.length) {
      moreCharts.forEach((ch) => {
        _y += ch[index]?.y || 0;
      });
    }

    chart.push({
      x,
      y: _y,
    });
  });

  return chart;
};

export const expressionChart = (
  stats,
  params: string[],
  expression: (params: number[]) => number,
  settings: {
    fill24?: boolean;
  } = {}
): AbstractChart[] => {
  const chart = [];
  if (!stats || !stats.length) return [];
  if (stats.length === 1) {
    // one day, chart based on hours
    const day = stats[0];
    const keys = Object.keys(day.hours);
    keys.forEach((hour) => {
      var x = +hour;
      const valuesByParams = params.map((param) => {
        let val = day.hours[hour][param];
        if (val && typeof val == 'object') val = sum(val);
        return val;
      });
      const val = expression(valuesByParams);
      const y = val || 0;
      chart.push({ x, y });
    });
    if (settings.fill24) {
      for (var x = (keys.length && +keys[keys.length - 1]) || 23; x < 24 - 1; x++) {
        chart.push({ x, y: 0 });
      }
    }
  } else {
    let maxX = 0;
    stats.forEach((day) => {
      let x = moment(day.end).dayOfYear();
      if (x >= maxX) maxX = x;
      else x += maxX;
      let y = 0;
      Object.keys(day.hours).forEach((hour) => {
        const valuesByParams = params.map((param) => {
          let val = day.hours[hour][param];
          if (val && typeof val == 'object') val = sum(val);
          return val;
        });
        const val = expression(valuesByParams);
        y += val || 0;
      });
      chart.push({ x, y });
    });
  }
  return chart;
};

/**
 * MULTIPLY - like simpleChart2 but multiply the stats instead of divide them
 * @returns {x: number, y: number}
 */
export const simpleChart4 = (chart1, chart2, settings: { chart1yMultiplier?: number } = {}) => {
  var { chart1yMultiplier } = settings;
  var chart = [];
  chart1.forEach(({ x, y }, index) => {
    var _y = chart2[index]?.y;
    if (_y) _y = y * (chart1yMultiplier || 1) * _y;
    else _y = 0;
    chart.push({
      x,
      y: _y,
    });
  });

  return chart;
};

// COUNTING ONLY DAYS WITH SPEND>0
export const dayCp = (stats, prop1, prop2) => {
  var days = [];
  var daysNotZeroSpend = 0;
  stats.forEach((day) => {
    var daySpend = 0;
    var dayCpPerHour = [];
    Object.values(day.hours).forEach((hour) => {
      var hourSpend = Number(hour[prop2]) || 0;
      var hourCp = Number(hour[prop1]);
      daySpend += hourSpend;
      dayCpPerHour.push(hourCp ? hourSpend / hourCp : 0);
    });
    if (daySpend) {
      var dayCpTotal = dayCpPerHour.reduce((t, v) => t + v, 0);
      var dayAverage = dayCpTotal ? daySpend / dayCpTotal : 0;
      days.push(dayAverage);
      daysNotZeroSpend++;
    }
  });
  var average = !daysNotZeroSpend ? 0 : days.reduce((t, v) => t + v, 0) / daysNotZeroSpend;
  return { average };
};

export const average = (stats, param, countZero = false) => {
  var count = 0;
  var agg = 0;
  stats.forEach((day) => {
    Object.values(day.hours).forEach((hour) => {
      const val = hour[param] || 0;
      if (!!val || countZero) {
        agg += val;
        count++;
      }
    });
  });
  //console.log('param', param, countZero,  count, agg, agg / count)
  return !count ? 0 : agg / count;
};

/**
 *
 * @param {number} _total the integer to distribute
 * @param {number} divider the number of parts you want to distribute (eg. 24 for one day)
 * @returns array of numbers
 */
export const distributeInteger = (_total, divider) => {
  const group = [];
  if (divider === 0) {
    group.push(0);
  } else {
    let sign = _total >= 0 ? 1 : -1;
    let total = Math.abs(_total);
    let rest = total % divider;
    let result = total / divider;

    for (let i = 0; i < divider; i++) {
      if (rest-- > 0) {
        group.push(Math.ceil(result) * sign);
      } else {
        group.push(Math.floor(result) * sign);
      }
    }
  }
  return group;
};

export const flattenObj = (ob) => {
  let result = {};
  for (const i in ob) {
    if (typeof ob[i] === 'object' && !Array.isArray(ob[i])) {
      const temp = flattenObj(ob[i]);
      for (const j in temp) {
        result[j] = temp[j];
      }
    } else {
      result[i] = ob[i];
    }
  }
  return result;
};

export const partialStateDependencies = {
  stats: getStats,
  paymentGatewayCosts: getPaymentGatewayCosts,
  shippingCosts: getShippingCosts,
  shipping: getShippingCostsType,
  customSpends: getCustomSpends,
  mainDatePickerSelectionRange: getMainDatePickerSelectionRange,
  totalInventoryItems: totalInventoryItems,
  totalInventoryItemsMissing: totalInventoryItemsMissing,
  totalInventoryCost: totalInventoryCost,
  totalInventoryValue: totalInventoryValue,
  shopTimezone: timezone,
  customMetrics: getCustomMetrics,
  willySummaryBox: willySummaryBox,
  newStats: getNewStats,
  activeShippingProvider: getActiveShippingProvider,
  ltv: (state) => state.ltv,
  msp: (state) => state.msp,
  customExpenseAttributionType: getCustomExpenseAttributionType,
  selectedStatsServcies: selectedStatsServices,
  customExpenseSmartAttribution: getCustomExpenseSmartAttribution,
  customSpendCategories: getCustomSpendCategories,
  useShippingChargesAsCost: useShippingChargesAsCost,
};

export const createPartialState = (values) => {
  const partialState: any = {};
  Object.keys(partialStateDependencies).forEach((key, i) => {
    partialState[key] = values[i];
  });
  return partialState;
};
